import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAdminPlans, createAdminPlan, updateAdminPlan, deleteAdminPlan } from '../../services/adminService';
import './AdminPage.css';

const BILLING_CYCLE_LABELS = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  annual: 'Anual',
  custom: 'Personalizado',
};

const fmt = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

// ── Limit definitions ──────────────────────────────────────────────────────
const FIXED_LIMITS = [
  { key: 'limit_banks',      label: 'Bancos' },
  { key: 'limit_cards',      label: 'Cartões de crédito' },
  { key: 'limit_categories', label: 'Categorias' },
  { key: 'limit_providers',  label: 'Fornecedores' },
  { key: 'limit_customers',  label: 'Clientes' },
];

const MONTHLY_LIMITS = [
  { key: 'limit_monthly_revenues',  label: 'Contas a receber / mês' },
  { key: 'limit_monthly_expenses',  label: 'Contas a pagar / mês' },
  { key: 'limit_monthly_transfers', label: 'Transferências / mês' },
];

const SYSTEM_LABELS = {
  financial: { label: 'Financeiro', icon: '💼', color: '#10b981', bg: '#d1fae5' },
  fleet:     { label: 'Frotas',     icon: '🚛', color: '#3b82f6', bg: '#dbeafe' },
  task:      { label: 'Tarefas',    icon: '📋', color: '#8b5cf6', bg: '#ede9fe' },
};

const EMPTY_LIMITS = {
  limit_banks: '',
  limit_cards: '',
  limit_categories: '',
  limit_providers: '',
  limit_customers: '',
  limit_monthly_revenues: '',
  limit_monthly_expenses: '',
  limit_monthly_transfers: '',
};

const EMPTY_FORM = {
  system: 'financial',
  name: '',
  description: '',
  price: '',
  billing_cycle: 'monthly',
  max_users: '',
  max_boards: '',
  features: '',
  is_active: true,
  ...EMPTY_LIMITS,
};

function formToPayload(form) {
  const limitField = (v) => (v === '' || v === null) ? null : parseInt(v);
  return {
    system: form.system || 'financial',
    name: form.name,
    description: form.description || null,
    price: parseFloat(form.price),
    billing_cycle: form.billing_cycle,
    max_users: form.max_users ? parseInt(form.max_users) : null,
    max_boards: form.system === 'task' && form.max_boards ? parseInt(form.max_boards) : null,
    features: form.features
      ? form.features.split('\n').map(f => f.trim()).filter(Boolean)
      : [],
    is_active: form.is_active,
    limit_banks:             form.system === 'financial' ? limitField(form.limit_banks) : null,
    limit_cards:             form.system === 'financial' ? limitField(form.limit_cards) : null,
    limit_categories:        form.system === 'financial' ? limitField(form.limit_categories) : null,
    limit_providers:         form.system === 'financial' ? limitField(form.limit_providers) : null,
    limit_customers:         form.system === 'financial' ? limitField(form.limit_customers) : null,
    limit_monthly_revenues:  form.system === 'financial' ? limitField(form.limit_monthly_revenues) : null,
    limit_monthly_expenses:  form.system === 'financial' ? limitField(form.limit_monthly_expenses) : null,
    limit_monthly_transfers: form.system === 'financial' ? limitField(form.limit_monthly_transfers) : null,
  };
}

function planToForm(plan) {
  const f = (v) => (v === null || v === undefined) ? '' : String(v);
  return {
    system: plan.system || 'financial',
    name: plan.name,
    description: plan.description || '',
    price: plan.price,
    billing_cycle: plan.billing_cycle,
    max_users: plan.max_users || '',
    max_boards: plan.max_boards || '',
    features: (plan.features || []).join('\n'),
    is_active: plan.is_active,
    limit_banks:             f(plan.limit_banks),
    limit_cards:             f(plan.limit_cards),
    limit_categories:        f(plan.limit_categories),
    limit_providers:         f(plan.limit_providers),
    limit_customers:         f(plan.limit_customers),
    limit_monthly_revenues:  f(plan.limit_monthly_revenues),
    limit_monthly_expenses:  f(plan.limit_monthly_expenses),
    limit_monthly_transfers: f(plan.limit_monthly_transfers),
  };
}

// ── LimitField: input with "Ilimitado" toggle ─────────────────────────────
function LimitField({ label, name, value, onChange }) {
  const isUnlimited = value === '';
  return (
    <div className="adm-form-group">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <input
          type="number"
          name={name}
          min="1"
          value={value}
          onChange={onChange}
          disabled={isUnlimited}
          placeholder="Ilimitado"
          style={{ flex: 1, minWidth: 0 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 400 }}>
          <input
            type="checkbox"
            checked={isUnlimited}
            onChange={(e) => onChange({ target: { name, value: e.target.checked ? '' : '1' } })}
          />
          Ilimitado
        </label>
      </div>
    </div>
  );
}

// ── Plan Modal ────────────────────────────────────────────────────────────
function PlanModal({ plan, onClose, onSaved }) {
  const isEdit = !!plan;
  const [form, setForm] = useState(isEdit ? planToForm(plan) : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' && name === 'is_active' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form);
      if (isEdit) {
        await updateAdminPlan(plan.id, payload);
      } else {
        await createAdminPlan(payload);
      }
      onSaved();
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Erro ao salvar plano.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>{isEdit ? 'Editar Plano' : 'Novo Plano'}</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="adm-modal-body">
            {error && <div className="adm-alert adm-alert-danger">{error}</div>}

            {/* ── Informações básicas ── */}
            <div className="plan-section-title">Informações do Plano</div>
            <div className="adm-form-grid">
              <div className="adm-form-group full-width">
                <label>Sistema *</label>
                <select name="system" value={form.system} onChange={handleChange} style={{ fontWeight: 600 }}>
                  <option value="financial">💼 Sistema Financeiro</option>
                  <option value="fleet">🚛 Gestão de Frotas</option>
                  <option value="task">📋 Gestão de Tarefas (Kanban)</option>
                </select>
              </div>
              <div className="adm-form-group full-width">
                <label>Nome do Plano *</label>
                <input name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div className="adm-form-group">
                <label>Valor (R$) *</label>
                <input name="price" type="number" step="0.01" min="0" value={form.price} onChange={handleChange} required />
              </div>
              <div className="adm-form-group">
                <label>Ciclo de Cobrança</label>
                <select name="billing_cycle" value={form.billing_cycle} onChange={handleChange}>
                  <option value="monthly">Mensal</option>
                  <option value="quarterly">Trimestral</option>
                  <option value="annual">Anual</option>
                  <option value="custom">Personalizado</option>
                </select>
              </div>
              <div className="adm-form-group">
                <label>Máx. Usuários</label>
                <input name="max_users" type="number" min="1" value={form.max_users} onChange={handleChange} placeholder="Ilimitado" />
              </div>
              {form.system === 'task' && (
                <div className="adm-form-group">
                  <label>Máx. Quadros (Kanban)</label>
                  <input name="max_boards" type="number" min="1" value={form.max_boards} onChange={handleChange} placeholder="Ilimitado" />
                </div>
              )}
              <div className="adm-form-group" style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: form.system === 'task' ? 0 : '1.5rem' }}>
                  <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                  Plano Ativo
                </label>
              </div>
              <div className="adm-form-group full-width">
                <label>Descrição</label>
                <textarea name="description" value={form.description} onChange={handleChange} rows={2} placeholder="Descrição resumida do plano" />
              </div>
            </div>

            {/* ── Limites específicos para o Sistema Financeiro ── */}
            {form.system === 'financial' && (
              <>
                <div className="plan-section-title" style={{ marginTop: '1rem' }}>
                  Cadastros Fixos
                  <span className="plan-section-hint">Quantidade total permitida de cada recurso</span>
                </div>
                <div className="adm-form-grid">
                  {FIXED_LIMITS.map(({ key, label }) => (
                    <LimitField key={key} name={key} label={label} value={form[key]} onChange={handleChange} />
                  ))}
                </div>

                <div className="plan-section-title" style={{ marginTop: '1rem' }}>
                  Lançamentos Mensais
                  <span className="plan-section-hint">Quantidade de lançamentos permitidos por mês</span>
                </div>
                <div className="adm-form-grid">
                  {MONTHLY_LIMITS.map(({ key, label }) => (
                    <LimitField key={key} name={key} label={label} value={form[key]} onChange={handleChange} />
                  ))}
                </div>
              </>
            )}

            {/* ── Funcionalidades ── */}
            <div className="plan-section-title" style={{ marginTop: '1rem' }}>Funcionalidades Incluídas</div>
            <div className="adm-form-group">
              <textarea
                name="features"
                value={form.features}
                onChange={handleChange}
                rows={4}
                placeholder={'Uma funcionalidade por linha:\nRelatórios avançados\nSuporte prioritário'}
              />
              <small style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Uma funcionalidade por linha</small>
            </div>
          </div>

          <div className="adm-modal-footer">
            <button type="button" className="adm-btn adm-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : (isEdit ? 'Salvar Alterações' : 'Criar Plano')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Plan Card ─────────────────────────────────────────────────────────────
function LimitBadge({ label, value }) {
  const display = (value === null || value === undefined) ? '∞' : value;
  return (
    <div className="plan-limit-badge">
      <span className="plan-limit-value">{display}</span>
      <span className="plan-limit-label">{label}</span>
    </div>
  );
}

export default function AdminPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [systemFilter, setSystemFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    getAdminPlans().then(setPlans).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => {
    setModal(null);
    load();
    setSuccessMsg('Plano salvo com sucesso!');
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  const handleDelete = async (plan) => {
    try {
      await deleteAdminPlan(plan.id);
      setDeleteConfirm(null);
      load();
      setSuccessMsg(`Plano "${plan.name}" removido.`);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao remover plano.');
    }
  };

  const filteredPlans = systemFilter === 'all'
    ? plans
    : plans.filter(p => (p.system || 'financial') === systemFilter);

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <div className="adm-breadcrumb"><Link to="/admin">Dashboard</Link> / Planos</div>
          <h2>Planos de Assinatura</h2>
        </div>
        <button className="adm-btn adm-btn-primary" onClick={() => setModal('create')}>
          + Novo Plano
        </button>
      </div>

      {/* Tabs de Filtro por Sistema */}
      <div className="adm-tabs" style={{ marginBottom: '1.25rem' }}>
        <button
          className={`adm-tab-btn ${systemFilter === 'all' ? 'active' : ''}`}
          onClick={() => setSystemFilter('all')}
        >
          🌐 Todos ({plans.length})
        </button>
        <button
          className={`adm-tab-btn ${systemFilter === 'financial' ? 'active' : ''}`}
          onClick={() => setSystemFilter('financial')}
        >
          💼 Financeiro ({plans.filter(p => (p.system || 'financial') === 'financial').length})
        </button>
        <button
          className={`adm-tab-btn ${systemFilter === 'fleet' ? 'active' : ''}`}
          onClick={() => setSystemFilter('fleet')}
        >
          🚛 Frotas ({plans.filter(p => p.system === 'fleet').length})
        </button>
        <button
          className={`adm-tab-btn ${systemFilter === 'task' ? 'active' : ''}`}
          onClick={() => setSystemFilter('task')}
        >
          📋 Tarefas ({plans.filter(p => p.system === 'task').length})
        </button>
      </div>

      {successMsg && (
        <div className="adm-alert" style={{ background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46' }}>
          ✓ {successMsg}
        </div>
      )}

      {loading ? (
        <div className="adm-loading">Carregando planos...</div>
      ) : filteredPlans.length === 0 ? (
        <div className="adm-empty">
          <div className="adm-empty-icon">📋</div>
          <p>{systemFilter === 'all' ? 'Nenhum plano cadastrado.' : 'Nenhum plano cadastrado para este sistema.'}</p>
          <button className="adm-btn adm-btn-primary" onClick={() => setModal('create')}>Criar novo plano</button>
        </div>
      ) : (
        <div className="plans-grid">
          {filteredPlans.map(plan => {
            const sys = SYSTEM_LABELS[plan.system || 'financial'] || SYSTEM_LABELS.financial;
            return (
              <div key={plan.id} className={`plan-card${plan.is_active ? '' : ' plan-card--inactive'}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: sys.bg, color: sys.color }}>
                    {sys.icon} {sys.label}
                  </span>
                  {!plan.is_active && (
                    <span className="adm-badge adm-badge-suspended" style={{ fontSize: '0.65rem' }}>Inativo</span>
                  )}
                </div>

                <div className="plan-card__header">
                  <div>
                    <span className="plan-card__name">{plan.name}</span>
                  </div>
                  <div className="plan-card__price">
                    <span className="plan-card__price-value">{fmt(plan.price)}</span>
                    <span className="plan-card__price-cycle">/{BILLING_CYCLE_LABELS[plan.billing_cycle]?.toLowerCase()}</span>
                  </div>
                </div>

                {plan.description && <p className="plan-card__desc">{plan.description}</p>}

                {/* Limites por Sistema */}
                {plan.system === 'task' ? (
                  <div className="plan-limits-section">
                    <div className="plan-limits-title">Limites de Uso</div>
                    <div className="plan-limits-grid">
                      <LimitBadge label="Quadros" value={plan.max_boards} />
                      <LimitBadge label="Usuários" value={plan.max_users} />
                    </div>
                  </div>
                ) : plan.system === 'fleet' ? (
                  <div className="plan-limits-section">
                    <div className="plan-limits-title">Limites de Uso</div>
                    <div className="plan-limits-grid">
                      <LimitBadge label="Usuários" value={plan.max_users} />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="plan-limits-section">
                      <div className="plan-limits-title">Cadastros fixos</div>
                      <div className="plan-limits-grid">
                        {FIXED_LIMITS.map(({ key, label }) => (
                          <LimitBadge key={key} label={label} value={plan[key]} />
                        ))}
                        <LimitBadge label="Usuários" value={plan.max_users} />
                      </div>
                    </div>

                    <div className="plan-limits-section">
                      <div className="plan-limits-title">Lançamentos / mês</div>
                      <div className="plan-limits-grid">
                        {MONTHLY_LIMITS.map(({ key, label }) => (
                          <LimitBadge key={key} label={label.replace(' / mês', '')} value={plan[key]} />
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {plan.features && plan.features.length > 0 && (
                  <ul className="plan-card__features">
                    {plan.features.map((f, i) => <li key={i}>✓ {f}</li>)}
                  </ul>
                )}

                <div className="plan-card__actions">
                  <button className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => setModal(plan)}>Editar</button>
                  <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setDeleteConfirm(plan)}>Remover</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <PlanModal
          plan={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}

      {deleteConfirm && (
        <div className="adm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="adm-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>Remover Plano</h3>
              <button className="adm-modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="adm-modal-body">
              <p>Tem certeza que deseja remover o plano <strong>{deleteConfirm.name}</strong>?</p>
              <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Esta ação não afeta assinaturas existentes.</p>
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn adm-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
              <button className="adm-btn adm-btn-danger" onClick={() => handleDelete(deleteConfirm)}>Remover</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
